import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  Cancel,
  EventBusy,
  CalendarMonth,
  Block,
  Close,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { format, differenceInDays, isBefore, startOfDay } from 'date-fns';
import leaveService, { 
  LeaveRequest, 
  MyLeaveBalanceResponse,
} from '../../services/leaveService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';

// Validation schema
const leaveRequestSchema = yup.object({
  leaveTypeId: yup.string().required('Leave type is required'),
  startDate: yup.date().required('Start date is required'),
  endDate: yup.date().required('End date is required')
    .min(yup.ref('startDate'), 'End date cannot be before start date'),
  reason: yup.string().required('Reason is required').min(10, 'Reason must be at least 10 characters'),
});

interface LeaveRequestFormData {
  leaveTypeId: string;
  startDate: Date | null;
  endDate: Date | null;
  reason: string;
}

const LeaveRequestPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [balanceData, setBalanceData] = useState<MyLeaveBalanceResponse | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState<LeaveRequest | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // Formik
  const formik = useFormik<LeaveRequestFormData>({
    initialValues: {
      leaveTypeId: '',
      startDate: null,
      endDate: null,
      reason: '',
    },
    validationSchema: leaveRequestSchema,
    onSubmit: async (values) => {
      try {
        await leaveService.createLeaveRequest({
          leaveTypeId: values.leaveTypeId,
          startDate: format(values.startDate!, 'yyyy-MM-dd'),
          endDate: format(values.endDate!, 'yyyy-MM-dd'),
          reason: values.reason,
        });
        enqueueSnackbar('Leave request submitted successfully', { variant: 'success' });
        handleCloseDialog();
        fetchData();
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to submit leave request';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
  });

  // Fetch leave balance — called once on mount and after write operations only
  const fetchBalance = useCallback(async () => {
    try {
      const balance = await leaveService.getMyLeaveBalance();
      setBalanceData(balance);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load leave balance', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Fetch requests — re-runs on search/filter/pagination changes
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const requestsData = await leaveService.getMyLeaveRequests({
        status: statusFilter || undefined,
        search: debouncedSearchTerm || undefined,
        page,
        limit: rowsPerPage,
      });
      setLeaveRequests(requestsData.data || []);
      setTotalCount(requestsData.pagination?.total || 0);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load leave requests', { variant: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [statusFilter, debouncedSearchTerm, page, rowsPerPage, enqueueSnackbar]);

  // On mount: fetch both balance and requests in parallel
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Convenience alias used after write operations (submit / cancel)
  const fetchData = useCallback(() => {
    fetchRequests();
    fetchBalance();
  }, [fetchRequests, fetchBalance]);

  // Handlers
  const handleOpenDialog = () => {
    formik.resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    formik.resetForm();
  };

  const handleOpenCancelDialog = (request: LeaveRequest) => {
    setCancellingRequest(request);
    setCancelDialogOpen(true);
  };

  const handleCancelRequest = async () => {
    if (!cancellingRequest) return;
    
    try {
      await leaveService.cancelLeaveRequest(cancellingRequest._id);
      enqueueSnackbar('Leave request cancelled', { variant: 'success' });
      setCancelDialogOpen(false);
      setCancellingRequest(null);
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to cancel request', { variant: 'error' });
    }
  };

  // Calculate days
  const calculateDays = () => {
    if (formik.values.startDate && formik.values.endDate) {
      const start = startOfDay(formik.values.startDate);
      const end = startOfDay(formik.values.endDate);
      if (!isBefore(end, start)) {
        return differenceInDays(end, start) + 1;
      }
    }
    return 0;
  };

  // Get available balance for selected leave type
  const getAvailableBalance = () => {
    if (!formik.values.leaveTypeId || !balanceData) return null;
    const balance = balanceData.balances.find(
      b => b.leaveTypeId._id === formik.values.leaveTypeId
    );
    return balance?.available ?? null;
  };

  // No client-side filtering - server handles it
  const displayedRequests = leaveRequests;

  // Render status chip
  const renderStatusChip = (status: string) => {
    const getStatusColor = (s: string) => {
      switch (s) {
        case 'PENDING': return theme.palette.warning.main;
        case 'APPROVED': return theme.palette.success.main;
        case 'REJECTED': return theme.palette.error.main;
        case 'CANCELLED': return theme.palette.grey[500];
        default: return theme.palette.warning.main;
      }
    };
    const color = getStatusColor(status);
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: alpha(color, 0.1),
          color: color,
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  };

  // Loading skeleton — only on the very first load
  if (initialLoad && loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  // No policy assigned
  if (!balanceData?.policy && !loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="70vh"
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 420,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Block 
            sx={{ 
              fontSize: 56, 
              color: 'text.disabled',
              mb: 2 
            }} 
          />
          <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
            No Leave Policy Assigned
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
             No leave policy has been assigned to your role yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
             Please contact your administrator to assign a policy.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Leave Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submit and track your leave requests
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenDialog}
            disabled={!balanceData?.policy}
          >
            Request Leave
          </Button>
        </Box>



        {/* Filters */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
              placeholder="Search by leave type or reason..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Requests Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {!initialLoad && loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Leave Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date Range</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Days</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                    <EventBusy sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || statusFilter ? 'No requests match your filters' : 'No leave requests yet'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedRequests.map((request) => {
                    const leaveType = typeof request.leaveTypeId === 'object' ? request.leaveTypeId : null;
                    return (
                      <TableRow key={request._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: leaveType?.color || theme.palette.grey[400],
                              }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                              {leaveType?.name || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(request.startDate), 'MMM dd, yyyy')}
                            {' - '}
                            {format(new Date(request.endDate), 'MMM dd, yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={`${request.totalDays} day${request.totalDays > 1 ? 's' : ''}`} size="small" />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip title={request.reason}>
                            <Typography variant="body2" noWrap>
                              {request.reason}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {renderStatusChip(request.status)}
                          {request.status === 'REJECTED' && request.rejectionReason && (
                            <Tooltip title={`Reason: ${request.rejectionReason}`}>
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                View reason
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(request.createdAt), 'MMM dd, yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {request.status === 'PENDING' && (
                            <Tooltip title="Cancel Request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenCancelDialog(request)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

        {/* Request Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>Request Leave</Typography>
            <IconButton size="small" onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth error={formik.touched.leaveTypeId && Boolean(formik.errors.leaveTypeId)}>
                    <InputLabel>Leave Type</InputLabel>
                    <Select
                      name="leaveTypeId"
                      value={formik.values.leaveTypeId}
                      label="Leave Type"
                      onChange={formik.handleChange}
                    >
                      {balanceData?.balances.map((balance) => {
                        const type = balance.leaveTypeId;
                        return (
                          <MenuItem key={type._id} value={type._id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: type.color }} />
                                {type.name}
                              </Box>
                              <Chip 
                                label={`${balance.available} days left`} 
                                size="small" 
                                sx={{ ml: 2, bgcolor: alpha(type.color, 0.1) }} 
                              />
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {formik.touched.leaveTypeId && formik.errors.leaveTypeId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {formik.errors.leaveTypeId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Start Date"
                    value={formik.values.startDate}
                    onChange={(date) => {
                      formik.setFieldValue('startDate', date);
                      // Auto-set end date if not set
                      if (!formik.values.endDate && date) {
                        formik.setFieldValue('endDate', date);
                      }
                    }}
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.startDate && Boolean(formik.errors.startDate),
                        helperText: formik.touched.startDate && formik.errors.startDate as string,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="End Date"
                    value={formik.values.endDate}
                    onChange={(date) => formik.setFieldValue('endDate', date)}
                    minDate={formik.values.startDate || new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.endDate && Boolean(formik.errors.endDate),
                        helperText: formik.touched.endDate && formik.errors.endDate as string,
                      },
                    }}
                  />
                </Grid>

                {/* Days calculation */}
                {calculateDays() > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Alert 
                      severity={
                        getAvailableBalance() !== null && calculateDays() > getAvailableBalance()!
                          ? 'error'
                          : 'info'
                      }
                      icon={<CalendarMonth />}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">
                          Total: <strong>{calculateDays()} day{calculateDays() > 1 ? 's' : ''}</strong>
                        </Typography>
                        {getAvailableBalance() !== null && (
                          <Typography variant="body2">
                            Available: <strong>{getAvailableBalance()} days</strong>
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="reason"
                    label="Reason"
                    value={formik.values.reason}
                    onChange={formik.handleChange}
                    error={formik.touched.reason && Boolean(formik.errors.reason)}
                    helperText={formik.touched.reason && formik.errors.reason}
                    placeholder="Please provide a reason for your leave request..."
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={() => formik.handleSubmit()}
              variant="contained"
              disabled={formik.isSubmitting || (getAvailableBalance() !== null && calculateDays() > getAvailableBalance()!)}
            >
              {formik.isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <ConfirmationDialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          onConfirm={handleCancelRequest}
          title="Cancel Leave Request?"
          message="Are you sure you want to cancel this leave request?"
          confirmText="Yes, Cancel Request"
          cancelText="No, Keep It"
          confirmColor="error"
        >
          {cancellingRequest && (
            <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Leave Type</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {typeof cancellingRequest.leaveTypeId === 'object' 
                        ? cancellingRequest.leaveTypeId.name 
                        : 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Dates</Typography>
                    <Typography variant="body2">
                      {format(new Date(cancellingRequest.startDate), 'MMM dd, yyyy')}
                      {' - '}
                      {format(new Date(cancellingRequest.endDate), 'MMM dd, yyyy')}
                      {' • '}
                      {cancellingRequest.totalDays} day{cancellingRequest.totalDays > 1 ? 's' : ''}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </ConfirmationDialog>
      </Box>
    </LocalizationProvider>
  );
};

export default LeaveRequestPage;
